import { useQuery, type ApolloError } from '@apollo/react-hooks';
import {
  type FundingSource,
  type ConfigurationMapping,
} from '../../../../Nomination/components';
import { PAGE_TEXT } from '../../../../Nomination/components/lang';
import { GET_APPLICATION_VIEW_FILTER_DATA } from './GET_APPLICATION_VIEW_FILTER_DATA.gql';
import { type ProgrammeChoiceMetadata } from '../../../graphql';
import { type DropDownSelectOption } from '../../../../../components';
import { APPLICANT_APPLIY_SCHOLARSHIPS } from '../../../../ScholarshipManagement/constants/fundingScheme';
import { type GSAssigneeList } from '../../../../Assessment/graphql';
import { MONITOR_LIST_TEXT } from '../../../../Assessment';

const fundingSourceValues = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.ABBV;

type GetApplicationViewFilterData = {
  fundingSources: DropDownSelectOption[];
  applicantApplyScholarships: DropDownSelectOption[];
  gsAssigneeOption: DropDownSelectOption[];
  programmeChoiceMetadata: ProgrammeChoiceMetadata;
  loading: boolean;
  error: ApolloError | undefined;
};

export const useGetApplicationViewFilterData =
  (): GetApplicationViewFilterData => {
    const { data, loading, error } = useQuery(
      GET_APPLICATION_VIEW_FILTER_DATA,
      {
        fetchPolicy: 'cache-and-network',
      },
    );

    const competitiveScheme =
      data?.getFundingSourceConfiguration?.mapping?.COMPETITIVE ?? [];
    const nonCompetitiveScheme =
      data?.getFundingSourceConfiguration?.mapping?.NON_COMPETITIVE ?? [];
    const otherScheme =
      data?.getFundingSourceConfiguration?.mapping?.OTHER ?? [];

    const fundingSourceConfiguration: ConfigurationMapping[] = [
      ...competitiveScheme,
      ...nonCompetitiveScheme,
      ...otherScheme,
    ];
    const fundingSources = fundingSourceConfiguration.map(config => {
      const value = config.value;
      const label =
        fundingSourceValues[value as keyof typeof fundingSourceValues];
      return { label, value };
    });

    const applicantApplyScholarships = fundingSourceConfiguration
      .filter(config => {
        return APPLICANT_APPLIY_SCHOLARSHIPS.includes(
          config.value as FundingSource,
        );
      })
      .map(config => {
        const value = config.value;
        const label = config.abbv || config.label;
        return { label, value };
      });

    const programmeChoiceMetadata: ProgrammeChoiceMetadata =
      data?.programmeChoiceMetadata ?? {
        departments: [],
        entryYears: [],
        entryYearList: [],
        prgCollabPhdPartnerList: [],
        rpgJoinPhdPartnerList: [],
      };

    const gsAssigneeList = (
      (data?.getGSAssigneeList?.assigneeList ?? []) as GSAssigneeList[]
    )?.map(assignee => ({
      value: assignee.netId,
      label: `${assignee.displayName} (${assignee.netId})`,
    }));

    gsAssigneeList.unshift({
      value: MONITOR_LIST_TEXT.UNASSIGNED,
      label: MONITOR_LIST_TEXT.UNASSIGNED,
    });

    return {
      fundingSources,
      applicantApplyScholarships,
      programmeChoiceMetadata,
      gsAssigneeOption: gsAssigneeList,
      loading,
      error,
    };
  };
